import { DetailSection } from '../common/detail-section'
import { DetailFact } from '../common/detail-fact'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { OperatorFaqBadgeTrigger } from '../operator-faq/operator-faq'
import { ATTENTION_LABEL } from './student-attention'
import { LIFECYCLE_LABEL } from './student-lifecycle'
import type { AttentionReason, LifecycleStage, StudentRow } from './types'

export function AttentionCell({
  reasons,
  className,
}: {
  reasons: AttentionReason[]
  className?: string
}): JSX.Element {
  if (reasons.length === 0) return <span className="text-text-secondary">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((reason) => (
        <AttentionBadge key={reason} reason={reason} className={className} />
      ))}
    </div>
  )
}

export function AttentionBadge({
  reason,
  className,
}: {
  reason: AttentionReason
  className?: string
}): JSX.Element {
  return (
    <OperatorFaqBadgeTrigger
      topicId={
        reason === 'continuity_delayed'
          ? 'continuity-waiting'
          : 'student-attention'
      }
      label={ATTENTION_LABEL[reason]}
      variant="danger"
      className={className}
    />
  )
}

export function LifecycleBadge({
  stage,
  className,
}: {
  stage: LifecycleStage
  className?: string
}): JSX.Element {
  return (
    <OperatorFaqBadgeTrigger
      topicId="student-lifecycle"
      label={LIFECYCLE_LABEL[stage]}
      variant="neutral"
      className={className}
    />
  )
}

export function StudentStatusPanel({
  student,
  createdLabel,
  lastPracticeLabel,
  notice,
}: {
  student: StudentRow
  createdLabel: string
  lastPracticeLabel: string
  notice?: ReactNode
}): JSX.Element {
  return (
    <DetailSection title="Student overview" notice={notice}
        meta={<section
          aria-label="Lifecycle and attention"
          className="flex min-w-0 flex-wrap items-center gap-2"
        >
          <LifecycleBadge stage={student.lifecycleStage} />
          {student.attentionReasons.length > 0 && (
            <ul className="flex min-w-0 flex-wrap gap-1 text-body">
              {student.attentionReasons.map((reason) => (
                <li key={reason}>
                  <AttentionBadge reason={reason} />
                </li>
              ))}
            </ul>
          )}
        </section>}
    >
      <DetailFact label="Email"><span className="break-all">{student.email}</span></DetailFact>
      <DetailFact label="Client"><Link to={`/admin/clients/${student.clientId}`} className="underline hover:text-text-primary">{student.clientName}</Link></DetailFact>
      <DetailFact label="Created">{createdLabel}</DetailFact>
      <DetailFact label="Last practice">{lastPracticeLabel}</DetailFact>
    </DetailSection>
  )
}
